/**
 * What the Settings › Warehouses table's cells read beyond their row (ACT2-1031).
 *
 * A context rather than closures in the column definitions: `Table` rebuilds its column defs whenever
 * the `columns` array changes identity, and `flexRender` uses each `cell` as a React element type, so
 * a column array rebuilt on every render remounts every cell — closing an open row menu and dropping
 * a Connect spinner. `useAdoptAccounts` answers a new object on every render, so the columns stay
 * fixed and the cells read the live state from here instead.
 */
import { createContext, useContext } from 'react';

import type { KebabMenuAction } from '@/_shared/components/list/KebabMenu';

import type { OrganizationWarehouseAccounts } from '../accountCopies/useOrganizationWarehouseAccounts';
import type { AdoptAccountsHook } from './adopt/useAdoptAccounts';
import type { WarehouseAccount } from './useWarehouseAccounts';
import type { ConnectionRow } from './warehouseList';

export interface WarehouseListContextValue {
  hook: AdoptAccountsHook;
  /** ACT2-1124: the organization's accounts of other workspaces, and their Connect (copy, then connect). */
  organization: OrganizationWarehouseAccounts;
  /** Connection uuid → its label, so a `connection_exists` refusal can name the connection. */
  connectionNames: Record<string, string>;
  /** ACT2-1276: why the RudderStack permission blocks every warehouse action, or null. */
  manageBlocked: string | null;
  rudderstackReadOnly: boolean;
  onReconnect: (account: WarehouseAccount) => void;
  onConnectAccount: (account: WarehouseAccount) => void;
  connectionActions: (row: ConnectionRow) => KebabMenuAction[];
}

export const WarehouseListContext = createContext<WarehouseListContextValue | null>(null);

export function useWarehouseListContext(): WarehouseListContextValue {
  const value = useContext(WarehouseListContext);
  if (value === null) throw new Error('Warehouse list cells must render inside WarehouseListContext');
  return value;
}
